import { useMutation } from "@tanstack/react-query";
import { useSetRecoilState } from "recoil";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { useNavigate } from "react-router-dom";
import {
  axiosInstancePromotion,
  CustomResponseFormat,
  setAuthHeadersPromotion,
} from "api/instancePromotion";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import mixpanel from "mixpanel-browser";
import { PATH } from "../routes/paths";
import useMixpanelPromotionPeople from "./useMixpanelPromotionPeople";
import useMixpanelPromotion from "./useMixpanelPromotion";
import useGtagConversion from "./useGtagConversion";
import { useState } from "react";

type SocialType = "google" | "apple";

interface LoginVars {
  idToken: string;
  email?: string;
}

export default function useLoginPromotionMutation(type: SocialType) {
  const navigate = useNavigate();
  const setUser = useSetRecoilState(userSimpleAtom);
  const setPromotionPeople = useMixpanelPromotionPeople();
  const trackGtagEvent = useGtagConversion();
  const trackPromotion = useMixpanelPromotion({ eventName: "가입" });
  const [error, setError] = useState<string>();

  const mutation = useMutation({
    mutationFn: async ({ idToken, email }: LoginVars) => {
      const response = await axiosInstancePromotion.post<CustomResponseFormat>(
        `/user/v2/login`,
        {
          idToken,
          type,
          ...(email && { email }),
        }
      );
      return response;
    },
    onSuccess: (response) => {
      if (response.code === 0) {
        const result: any = response.result;
        setAuthHeadersPromotion(result.accessToken);
        setUser(result);

        // Meta CAPI
        trackCapiStandardEvent("CompleteRegistration", {
          externalId: result.userId,
          email: result.email,
        });

        // Google Ads
        trackGtagEvent("CompleteRegistration", "v4");

        // Mixpanel identify & people
        const promotionInstance = (mixpanel as any).promotion;
        if (promotionInstance && result.trackId) {
          promotionInstance.identify(result.trackId);
          trackPromotion();
          setPromotionPeople({
            $email: result.email,
            user_id: result.userId,
          });
        }

        // Navigate next
        if (result.nickname) {
          navigate(PATH.promotionPayment, { replace: true });
        } else {
          navigate(PATH.promotionNicknameSubmit, { replace: true });
        }
      } else {
        setError(
          `로그인에 실패했습니다. 다시 시도해주세요.: ${response.code}`
        );
      }
    },
    onError: (e: any) => {
      console.error("Promotion login failed:", e);
      setError(`로그인에 실패했습니다. 다시 시도해주세요.`);
    },
  });

  return {
    login: mutation.mutate,
    loginAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error,
  };
}
